import { type VizijAssetBundle } from "@vizij/runtime-react";

export type FaceBase = {
  kind: string;
  name: string;
};

export type TimedViseme = {
  time: number;
  type: string;
  value: string;
};

export type AmplitudePoint = {
  time: number;
  value: number;
};

export type PixiAvatar = FaceBase & {
  kind: "PixiFace";
  type: "avatar";
  mouth: "Hugo" | "ZigZag" | "Ellipse" | "Beak";
  eyes: { kind: "spritesheet"; path: string };
  cheeks: { kind: "texture"; path: string } | null;
  eyebrows: "Hugo" | null;
};

export type PixiText = FaceBase & {
  kind: "PixiFace";
  type: "text";
  showEmotion: "emoticon" | "emoji" | "none";
  showText: boolean;
};

export type PixiFace = PixiAvatar | PixiText;

export const builtInFaces: PixiFace[] = [
  {
    kind: "PixiFace",
    type: "avatar",
    name: "Hugo",
    mouth: "Hugo",
    eyes: {
      kind: "spritesheet",
      path: "/FaceAssets/Hugo/hugo_spritesheet.json",
    },
    cheeks: { kind: "texture", path: "/FaceAssets/Hugo/cheek.png" },
    eyebrows: "Hugo",
  },
  {
    kind: "PixiFace",
    type: "avatar",
    name: "Simple",
    mouth: "Ellipse",
    eyes: {
      kind: "spritesheet",
      path: "/FaceAssets/Hugo/hugo_spritesheet.json",
    },
    cheeks: null,
    eyebrows: null,
  },
  {
    kind: "PixiFace",
    type: "avatar",
    name: "Robotic",
    mouth: "ZigZag",
    eyes: {
      kind: "spritesheet",
      path: "/FaceAssets/Robotic/robotic_spritesheet.json",
    },
    cheeks: null,
    eyebrows: null,
  },
  {
    kind: "PixiFace",
    type: "text",
    name: "Display",
    showEmotion: "emoticon",
    showText: true,
  },
];

export const defaultFace: PixiFace = builtInFaces[0];

export type VizijSettings = {
  faceExpressivity: number;
};

export type PixiSettings = {
  eyeDisplay: "default" | "none";
  mouthDisplay: "default" | "none";
  cheekDisplay: "default" | "none";
  emotionDisplay: "emoticon" | "emoji" | "none";
};

export type FaceSettings = {
  zoom: number;
  offset: { x: number; y: number };
  rotation: number;
  autoRotate: boolean;
  showFullScreenButton: boolean;
  showScrollButton: boolean;
  vizijSettings?: VizijSettings;
  pixiSettings?: PixiSettings;
  selectedLanguage: string | null;
  speechQueueMode?: "interrupt" | "queue";
  captions: {
    enabled: boolean;
    fontSize: number;
    offset: { x: number; y: number };
  };
};

export const defaultFaceSettings: FaceSettings = {
  zoom: 1,
  offset: { x: 0, y: 0 },
  rotation: 0,
  autoRotate: true,
  showFullScreenButton: true,
  showScrollButton: true,
  vizijSettings: {
    faceExpressivity: 1,
  },
  pixiSettings: {
    eyeDisplay: "default",
    mouthDisplay: "default",
    cheekDisplay: "default",
    emotionDisplay: "emoticon",
  },
  selectedLanguage: "English",
  speechQueueMode: "interrupt",
  captions: {
    enabled: false,
    fontSize: 32,
    offset: { x: 0, y: 0 },
  },
};

export type VizijFace = FaceBase & {
  kind: "Vizij";
  assetBundle: VizijAssetBundle;
};

const HugoVizij: VizijFace = {
  kind: "Vizij",
  name: "Hugo - Expressive",
  assetBundle: {
    namespace: "hugo",
    glb: {
      kind: "url",
      src: "/FaceAssets/Vizij/Hugo.glb",
      aggressiveImport: true,
    },
  },
};

export const customFace: VizijFace = {
  kind: "Vizij",
  name: "Custom Face",
  assetBundle: {
    namespace: "custom",
    glb: {
      kind: "url",
      src: "",
      aggressiveImport: true,
    },
  },
};

export const includedVizijFaces: VizijFace[] = [HugoVizij, customFace];

export const availableFaces: Face[] = [...builtInFaces, ...includedVizijFaces];

// export type CustomFace = VizijFace;
export type Face = PixiFace | VizijFace;

export type FaceCapabilities = {
  canExpressColor: boolean | string;
  canExpressEmotion: boolean | string;
  canLipSync: boolean | string;
};

export const getFaceCapabilities = (face?: Face): FaceCapabilities => {
  if (!face) {
    return {
      canExpressColor: false,
      canExpressEmotion: false,
      canLipSync: false,
    };
  }
  if (face.kind === "PixiFace") {
    if (face.type === "avatar") {
      return {
        canExpressColor: "Background Color Change",
        canExpressEmotion: "Image Swap",
        canLipSync:
          face.mouth === "ZigZag"
            ? "Random mouth motions"
            : "Timed mouth motions",
      };
    }
    if (face.type === "text") {
      return {
        canExpressColor: "Background Color Change",
        canExpressEmotion:
          face.showEmotion === "none" ? false : face.showEmotion,
        canLipSync: "Cursor movement",
      };
    }
  }
  if (face.kind === "Vizij") {
    return {
      // TODO: Check the face has the relevant rigs for color, emotion, and lip sync
      canExpressColor: "Background Color Change",
      canExpressEmotion: "Animated Expression",
      canLipSync: "Lip-sync or random mouth motions",
    };
  }
  return {
    canExpressColor: false,
    canExpressEmotion: false,
    canLipSync: false,
  };
};
